import { DataArrayRounded, DataObjectRounded, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Chip, Collapse, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { memo, useMemo, useState } from 'react'
import { JsonSchema } from '../../../definitions/Nodes'

const getTypeColor = (type: string | undefined) => {
    if (typeof type === 'undefined') return 'default'
    switch (type) {
        case 'string':
            return 'primary'
        case 'number':
            return 'audit'
        case 'boolean':
            return 'subFlow'
        case 'array':
            return 'warning'
        case 'object':
            return 'dryRun'
        default:
            return 'default'
    }
}

const RecursiveRow = memo(({ entry, level }: { entry: [string, JsonSchema], level: number }) => {
    const [open, setOpen] = useState(false)

    const hasNestedArrayProperties = entry[1]?.type === 'array' && entry[1].items?.properties
    const hasNestedObjectProperties = entry[1]?.type === 'object' && entry[1].properties

    const chipLabel = entry[1]?.format || entry[1]?.type || 'undefined'
    const chipColor = getTypeColor(entry[1]?.type ?? 'default')

    return (
        <>
            <TableRow>
                <TableCell>
                    {entry[0]}{' '}
                    {(hasNestedArrayProperties || hasNestedObjectProperties) && (
                        <IconButton size='small' onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUp fontSize='small' /> : <KeyboardArrowDown fontSize='small' />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell align='right'>
                    <Chip
                        label={chipLabel}
                        color={chipColor}
                        size='small'
                        variant={level === 0 ? 'filled' : 'outlined'}
                        sx={{ width: 80 }}
                    />
                </TableCell>
            </TableRow>
            {(hasNestedArrayProperties || hasNestedObjectProperties) && (
                <TableRow>
                    <TableCell colSpan={2} sx={{ padding: 0, paddingLeft: 2 }}>
                        <Collapse in={open} timeout='auto' unmountOnExit>
                            <Table size='small'>
                                <TableBody>
                                    {hasNestedArrayProperties &&
                                        Object.entries(entry[1].items.properties).map((nestedEntry, index) => (
                                            <RecursiveRow key={`nestedArray-${index}`} entry={nestedEntry} level={level + 1} />
                                        ))}
                                    {hasNestedObjectProperties &&
                                        Object.entries(entry[1].properties).map((nestedEntry, index) => (
                                            <RecursiveRow key={`nestedObject-${index}`} entry={nestedEntry} level={level + 1} />
                                        ))}
                                </TableBody>
                            </Table>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    )
})

RecursiveRow.displayName = 'RecursiveRow'

const RenderTableSchema = ({ data, maxHeight = undefined }: { data: JsonSchema, maxHeight?: number }) => {
    const entries = useMemo(() => {
        return data?.type === 'object' && data?.properties
            ? Object.entries(data.properties)
            : data?.type === 'array' && data?.items?.properties
                ? Object.entries(data.items.properties)
                : []
    }, [data])

    return (
        <TableContainer component={Paper} sx={{ maxHeight }}>
            <Table stickyHeader size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Type {data?.type === 'object' ? <DataObjectRounded fontSize='small' /> : data?.type === 'array' ? <DataArrayRounded fontSize='small' /> : null}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map((entry, index) => (
                        <RecursiveRow key={index} entry={entry} level={0} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default memo(RenderTableSchema)
